import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams } from '../http-params.util';

/**
 * CustomerServiceApiService (Angular 18) — "Sherpa" customer-service tools.
 *
 * Replaces apps/b2b-ui/common/services/api/customerServiceApi/customerServiceApi.service.js.
 */
const SHERPA_BASE = '/v1/api/sherpa';
const CRON_URL = '/v1/api/cron';
const B2B_REFUNDS_URL = '/v1/b2b/refunds';

@Injectable({ providedIn: 'root' })
export class CustomerServiceApiService {
  private readonly http = inject(HttpClient);

  listTenants(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${SHERPA_BASE}/tenants`));
  }

  listCustomers(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${SHERPA_BASE}/visitors`));
  }

  searchCustomers(like: string, emailLike: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/visitors`, {
        params: buildParams({ like, emailLike }),
      }),
    );
  }

  getAllOrders(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${SHERPA_BASE}/orders`));
  }

  getOrder(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${SHERPA_BASE}/orders/${id}`));
  }

  getOrdersForTenant(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/orders`, {
        params: buildParams({ tenantId: id }),
      }),
    );
  }

  getOrdersForTenantLike(id: number | string, like: string, emailLike: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/orders`, {
        params: buildParams({ tenantId: id, like, emailLike }),
      }),
    );
  }

  getOrdersLike(like?: string, emailLike?: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/orders`, {
        params: buildParams({ like, emailLike }),
      }),
    );
  }

  getOrdersForVisitor(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/orders`, {
        params: buildParams({ visitorId: id }),
      }),
    );
  }

  // Note: legacy quirk — only the `like` querystring is forwarded, not
  // `emailLike`, despite both being accepted as parameters. Preserved 1:1.
  getOrdersForVisitorLike(id: number | string, like: string, _emailLike: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/orders`, {
        params: buildParams({ visitorId: id, like }),
      }),
    );
  }

  cron(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(CRON_URL));
  }

  deputySignIn(tenantId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${SHERPA_BASE}/tenants/${tenantId}/deputy`));
  }

  getTenantB2BActivity(tenantId?: string, deputyId?: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/b2blog`, {
        params: buildParams({ tenantId, deputyId }),
      }),
    );
  }

  getTenantB2CActivity(tenantId?: string, deputyId?: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${SHERPA_BASE}/b2clog`, {
        params: buildParams({ tenantId, deputyId }),
      }),
    );
  }

  refund(refundObject: unknown): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(B2B_REFUNDS_URL, refundObject));
  }
}
